import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useBrand } from '../../context/BrandContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Stack,
  IconButton,
  Tooltip,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  TrendingUp as InIcon,
  TrendingDown as OutIcon,
  SwapHoriz as TransferIcon,
  Receipt as ReceiptIcon,
  LocalShipping as ShipmentIcon,
  AssignmentReturn as ReturnIcon,
  Delete as DisposalIcon,
  Edit as AdjustmentIcon,
  Close as CloseIcon,
  FactCheck as StockCountIcon,
  OpenInNew as OpenInNewIcon,
  EditOutlined as EditOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
  VerifiedUser as TrustPointIcon
} from '@mui/icons-material';
import {
  getDailyAggregatedMovements,
  getDailyMovementDetails,
  getWarehouses,
  formatNumber,
  getCalculatedStockLevels,
  getStockBreakdownFromTrustPoint,
  updateDisposal,
  deleteDisposal,
  getDisposal
} from '../../services/inventoryService';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot
} from 'recharts';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

// Movement type configuration
const MOVEMENT_TYPES = {
  goods_receipt: {
    label: 'Goods Receipt',
    icon: ReceiptIcon,
    color: 'success',
    direction: 'in'
  },
  order_fulfillment: {
    label: 'Order Fulfillment',
    icon: ShipmentIcon,
    color: 'primary',
    direction: 'out'
  },
  customer_shipment: {
    label: 'Customer Shipment',
    icon: ShipmentIcon,
    color: 'primary',
    direction: 'out'
  },
  retail_shipment: {
    label: 'Retail/B2B Shipment',
    icon: ShipmentIcon,
    color: 'secondary',
    direction: 'out'
  },
  fba_transfer: {
    label: 'FBA Transfer',
    icon: TransferIcon,
    color: 'warning',
    direction: 'out'
  },
  transfer_out: {
    label: 'Transfer Out',
    icon: TransferIcon,
    color: 'warning',
    direction: 'out'
  },
  transfer_in: {
    label: 'Transfer In',
    icon: TransferIcon,
    color: 'info',
    direction: 'in'
  },
  return: {
    label: 'Customer Return',
    icon: ReturnIcon,
    color: 'info',
    direction: 'in'
  },
  disposal: {
    label: 'Disposal',
    icon: DisposalIcon,
    color: 'error',
    direction: 'out'
  },
  adjustment_increase: {
    label: 'Adjustment (+)',
    icon: AdjustmentIcon,
    color: 'success',
    direction: 'in'
  },
  adjustment_decrease: {
    label: 'Adjustment (-)',
    icon: AdjustmentIcon,
    color: 'error',
    direction: 'out'
  },
  shiphero_adjustment: {
    label: 'ShipHero Adjustment',
    icon: AdjustmentIcon,
    color: 'warning',
    direction: 'neutral'
  },
  production_consumption: {
    label: 'Production Consumption',
    icon: AdjustmentIcon,
    color: 'secondary',
    direction: 'out'
  },
  fba_stock_change: {
    label: 'FBA Stock Change',
    icon: TransferIcon,
    color: 'info',
    direction: 'in'  // direction determined dynamically by sign of quantity
  },
  stock_count_report: {
    label: 'Stock Count',
    icon: StockCountIcon,
    color: 'default',
    direction: 'neutral'
  }
};

function InventoryMovements() {
  const { selectedBrand, brands, selectBrand } = useBrand();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Race guard: filling SKU/warehouse/date filters fires loadData several times
  // in quick succession (an early unfiltered full-brand query can still be in
  // flight when the filtered one returns). Without a guard, a slow/failing
  // EARLIER run resolves AFTER the good one and paints "Failed to load…" over
  // already-rendered rows (Rishabh, 2026-06-09: banner that clears on refresh).
  // Each loadData claims a sequence number; only the latest may touch state.
  const loadSeqRef = useRef(0);
  // The chart + running-balance LEDGER depends only on SKU / warehouse / date window —
  // NOT on the movement-type filter or pagination. The type filter and paging are
  // display-only: they change which table rows are shown and which chart points are
  // highlighted, but they must NEVER reshape the chart or recompute the balance off a
  // partial set. We cache the ledger's identity key + the full-ledger balance map so a
  // type/page change neither refetches nor rebuilds the chart — it only re-renders the
  // table rows (balances looked up from the cached full ledger) and re-highlights points.
  // (2026-06-24 — fixes graph reshaping on filter + running balance computed off the
  // filtered subset instead of all movements.)
  const graphKeyRef = useRef(null);
  const balanceMapRef = useRef(null);
  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [warehouses, setWarehouses] = useState([]);

  // Initialize filters from URL params on first render
  const skuParam = searchParams.get('sku');
  const warehouseParam = searchParams.get('warehouse');
  const fromCountDateParam = searchParams.get('from_count_date');
  const trustPointTypeParam = searchParams.get('trust_point_type');
  const trustPointIdParam = searchParams.get('trust_point_id');
  const brandParam = searchParams.get('brand');
  const initialBrandId = brandParam ? parseInt(brandParam, 10) : null;

  // Filters - initialize from URL params
  const [searchTerm, setSearchTerm] = useState(skuParam || '');
  const [movementTypeFilter, setMovementTypeFilter] = useState('all');
  const [warehouseFilter, setWarehouseFilter] = useState(warehouseParam || 'all');
  const [dateFrom, setDateFrom] = useState(fromCountDateParam || '');
  const [dateTo, setDateTo] = useState('');
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // Drill-down dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [orderDetails, setOrderDetails] = useState([]);
  const [disposalEditDialog, setDisposalEditDialog] = useState({ open: false, data: null, id: null });
  const [disposalDeleteDialog, setDisposalDeleteDialog] = useState({ open: false, detail: null });
  const [disposalActionError, setDisposalActionError] = useState(null);
  // DT-1939: the Details dialog's own load failure, shown inside it (the page Alert sits behind the modal)
  const [detailsError, setDetailsError] = useState(null);
  const [disposalActionSuccess, setDisposalActionSuccess] = useState(null);

  // Running balance state
  const [currentStock, setCurrentStock] = useState(null);
  const [runningBalances, setRunningBalances] = useState([]);
  // Separate graph data: when trust point is active, graph needs ALL movements (unpaginated)
  const [graphMovements, setGraphMovements] = useState([]);
  const [graphBalances, setGraphBalances] = useState([]);

  // Sync brand from URL on mount so shared /inventory/movements links resolve the
  // SKU against the brand the link was built for, not the recipient's default brand.
  // URL takes priority over context; runs once (mirrors StockLevels.js).
  const brandFromUrlApplied = useRef(false);
  useEffect(() => {
    if (initialBrandId && brands.length > 0 && !brandFromUrlApplied.current) {
      const brandFromUrl = brands.find(b => b.id === initialBrandId);
      if (brandFromUrl) {
        brandFromUrlApplied.current = true;
        selectBrand(brandFromUrl);
      }
    }
  }, [initialBrandId, brands, selectBrand]);

  // Update filters when URL params change (after initial render)
  useEffect(() => {
    if (selectedBrand?.id) {
      const newSkuParam = searchParams.get('sku');
      const newWarehouseParam = searchParams.get('warehouse');

      if (newSkuParam !== null && newSkuParam !== searchTerm) {
        setSearchTerm(newSkuParam);
      }
      if (newWarehouseParam !== null && newWarehouseParam !== warehouseFilter) {
        setWarehouseFilter(newWarehouseParam);
      }
      setFiltersInitialized(true);
    }
  }, [selectedBrand?.id, searchParams]);

  // Load data when filters change (only after filters are initialized)
  useEffect(() => {
    if (selectedBrand?.id && filtersInitialized) {
      loadData();
    }
  }, [selectedBrand?.id, page, rowsPerPage, searchTerm, movementTypeFilter, warehouseFilter, dateFrom, dateTo, filtersInitialized]);

  useEffect(() => {
    if (selectedBrand?.id) {
      loadWarehouses();
    }
  }, [selectedBrand?.id]);

  const loadWarehouses = async () => {
    try {
      const response = await getWarehouses({ is_active: true });
      // Backend returns { success: true, warehouses: [...] }
      setWarehouses(response?.warehouses || response?.data || []);
    } catch (err) {
      console.error('Failed to load warehouses:', err);
    }
  };

  const getWarehouseName = (code) => {
    const wh = warehouses.find(w => w.code === code || w.warehouse_code === code);
    const nm = wh && (wh.name || wh.warehouse_name);
    return nm ? `${nm} (${code})` : (code || 'N/A');
  };

  const loadData = async () => {
    if (!selectedBrand?.id) {
      setError('Please select a brand first');
      setLoading(false);
      return;
    }

    const seq = ++loadSeqRef.current;  // claim latest; stale runs bail before setState
    setLoading(true);
    setError(null);

    // The chart/ledger identity. Only a change to SKU / warehouse / date window should
    // reshape (or blank) the chart. A movement-type or pagination change keeps the same
    // ledger — clearing it here is what made the graph rebuild "again and again".
    const shouldFetchStock = searchTerm && warehouseFilter !== 'all';
    // Identity MUST include the brand and the trust-point anchor — otherwise switching
    // brand (or trust point) while the SKU/warehouse text is unchanged would silently
    // reuse the previous brand's ledger and show a stale chart + wrong balance.
    const graphKey = `${selectedBrand?.id}|${searchTerm}|${warehouseFilter}|${dateFrom}|${dateTo}|${fromCountDateParam || ''}`;
    const graphKeyChanged = graphKey !== graphKeyRef.current;
    if (!shouldFetchStock || graphKeyChanged) {
      setCurrentStock(null);
      setRunningBalances([]);
      setGraphMovements([]);
      setGraphBalances([]);
      balanceMapRef.current = null;
      graphKeyRef.current = null;
    }

    try {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage
      };

      if (searchTerm) params.sku = searchTerm;
      if (movementTypeFilter !== 'all') params.movement_type = movementTypeFilter;
      if (warehouseFilter !== 'all') params.warehouse_code = warehouseFilter;
      // The date selected at the top controls the window — show ALL movements and
      // counts on/after it (no longer clamped to the trust point). dateFrom still
      // DEFAULTS to the trust point (fromCountDateParam) so the first view anchors
      // at the baseline, but the operator can pick an earlier date to see pre-count
      // history. The running balance re-anchors at each count (see below), so it
      // stays correct even across the trust point. (Vishal 2026-06-02)
      const effectiveDateFrom = dateFrom;
      if (effectiveDateFrom) params.date_from = effectiveDateFrom;
      if (dateTo) params.date_to = dateTo;

      // shouldFetchStock + graphKey/graphKeyChanged were computed above (the running
      // balance + chart only apply when filtering to a specific SKU AND warehouse).

      // Single scan: the daily endpoint now returns the per-type summary alongside
      // the rows (computed from the SAME movements the grid uses), so we no longer
      // fire a second full-scan summary query concurrently. Cards always match the grid.
      const movementsRes = await getDailyAggregatedMovements(selectedBrand.id, params);

      // A newer loadData started while this awaited — drop this stale result so
      // it can't overwrite the latest run's data (or, on failure, its banner).
      if (seq !== loadSeqRef.current) return;

      const movementsList = movementsRes?.data?.movements || [];
      const totalMovements = movementsRes?.data?.pagination?.total || 0;
      setMovements(movementsList);
      setTotalCount(totalMovements);

      // Process summary data - convert from array to object format
      const summaryArray = movementsRes?.data?.summary || [];
      if (Array.isArray(summaryArray) && summaryArray.length > 0) {
        // Transform array of {type, count, total_qty} to object with named properties
        const summaryObj = {
          inbound_count: 0,
          outbound_count: 0,
          fulfillments_count: 0,
          receipts_count: 0
        };

        summaryArray.forEach(item => {
          const count = parseInt(item.count) || 0;
          switch (item.type) {
            case 'goods_receipt':
              summaryObj.receipts_count = count;
              summaryObj.inbound_count += count;
              break;
            case 'transfer_in':
            case 'return':
            case 'adjustment_increase':
              summaryObj.inbound_count += count;
              break;
            case 'customer_shipment':
            case 'retail_shipment':
              summaryObj.fulfillments_count += count;
              summaryObj.outbound_count += count;
              break;
            case 'transfer_out':
            case 'disposal':
            case 'fba_transfer':
            case 'adjustment_decrease':
              summaryObj.outbound_count += count;
              break;
            case 'fba_stock_change':
              // FBA deltas can be positive or negative — count as both
              summaryObj.outbound_count += count;
              break;
            default:
              break;
          }
        });

        setSummary(summaryObj);
      } else {
        setSummary(null);
      }

      // Build the FULL-LEDGER balance and chart data, then map each visible table row's
      // balance from it. The ledger (chart shape + running balance) depends only on
      // SKU/warehouse/date — not on the movement-type filter or pagination — so it is
      // fetched ONLY when its identity (graphKey) changes. A type/page change reuses the
      // cached ledger: the table just re-maps its visible rows and the chart re-highlights.
      const balKey = (m) => `${m.movement_date}|${m.movement_type}|${m.order_source || ''}`;
      if (shouldFetchStock && movementsList.length > 0) {
        try {
          let stockQty;
          let balanceMap;

          if (graphKeyChanged || !balanceMapRef.current) {
            // ---- Ledger identity changed (new SKU / warehouse / date) → (re)fetch ----
            stockQty = 0;

            // When a trust point with type/id is provided (from StockBreakdownDialog),
            // use the breakdown endpoint which handles all trust point types
            // (physical counts, goods receipts, transfers in).
            // The generic getCalculatedStockLevels only supports physical count trust points.
            if (fromCountDateParam && trustPointTypeParam && trustPointIdParam) {
              const trustPoint = {
                date: fromCountDateParam,
                type: trustPointTypeParam,
                id: parseInt(trustPointIdParam)
              };
              const breakdownRes = await getStockBreakdownFromTrustPoint(
                searchTerm, warehouseFilter, trustPoint,
                { brand_id: selectedBrand.id }
              );
              stockQty = parseFloat(breakdownRes?.data?.quantity) || 0;
            } else {
              const stockParams = {
                brand_id: selectedBrand.id,
                sku: searchTerm,
                warehouse_code: warehouseFilter
              };
              if (fromCountDateParam) {
                stockParams.from_count_date = fromCountDateParam;
              }
              const stockRes = await getCalculatedStockLevels(stockParams);

              // Extract current stock for the specific SKU/warehouse
              const items = stockRes?.data?.items || stockRes?.items || [];
              for (const item of items) {
                if (item.primary_sku?.toUpperCase() === searchTerm.toUpperCase()) {
                  if (item.warehouses) {
                    const wh = item.warehouses.find(w => w.warehouse_code === warehouseFilter);
                    if (wh) {
                      stockQty = wh.quantity || wh.current_quantity || 0;
                      break;
                    }
                  } else if (item.total_quantity !== undefined) {
                    stockQty = item.total_quantity;
                    break;
                  }
                }
              }
            }

            // The chart shows full history (anchored to current stock and walking
            // backward through every movement). Fetch a separate dataset with
            // include_full_history=true — the backend's trust-point auto-cutoff applies
            // to the table fetch but the chart must span pre-trust-point movements too.
            // CRUCIALLY: the movement-type filter is DELIBERATELY NOT applied here. The
            // chart + running balance must be computed over ALL movement types so the
            // shape is stable across filtering and the balance reflects every movement.
            // The type filter only highlights points and filters the table rows below.
            let graphData = movementsList;
            try {
              const graphParams = {
                limit: 10000,
                offset: 0,
                sku: searchTerm,
                warehouse_code: warehouseFilter,
                include_full_history: true
              };
              if (dateTo) graphParams.date_to = dateTo;
              // Floor the chart's left edge at the LATER of:
              //   - brand acquisition_date — pre-acquisition movements are the previous owner's,
              //     and walking backward through them inflates projected stock to fictional levels
              //     (e.g. SS0000012010@G3PL projected 120K because 1,357 outbound deductions
              //     became additions when walked back through pre-acquisition history).
              //   - the user's From Date filter — their explicit intent should constrain the chart
              //     too, not just the table.
              // Backend honors date_from even when include_full_history=true (see
              // inventory-movements-service.js:1798 effectiveCutoff).
              const acquisitionFloor = selectedBrand?.acquisition_date || null;
              const userFloor = dateFrom || null;
              const chartFloor = (acquisitionFloor && userFloor)
                ? (userFloor > acquisitionFloor ? userFloor : acquisitionFloor)
                : (userFloor || acquisitionFloor);
              if (chartFloor) graphParams.date_from = chartFloor;
              const graphRes = await getDailyAggregatedMovements(selectedBrand.id, graphParams);
              graphData = graphRes?.data?.movements || movementsList;
              // Backend's date_from filter has an OR exception that always passes through
              // stock_count_report rows (inventory-movements-service.js:1802 — "stock_count_report
              // is an observation marker, always passes through"). For the chart we want EVERY
              // row (including reported-stock diamonds) to respect the floor, otherwise diamonds
              // pre-acquisition or pre-user-filter leak in and skew the X-axis. Post-filter here.
              if (chartFloor) {
                graphData = graphData.filter(m => !m.movement_date || String(m.movement_date) >= chartFloor);
              }
            } catch (graphErr) {
              console.error('Failed to load full graph data:', graphErr);
              // Fall back to paginated table data
            }

            if (seq !== loadSeqRef.current) return;
            setCurrentStock(stockQty);

            // Full-ledger balances (data comes sorted by date DESC, newest first). Walk
            // backwards from current stock, subtracting each movement's net quantity, and
            // record the balance AFTER each movement keyed by (date|type|source) so the
            // table can look up any row's true balance regardless of filter/pagination.
            // stock_count_report is an observation (absolute count), NOT a delta and NOT
            // trusted — never re-anchor the balance to a count. (Vishal 2026-06-02.)
            if (graphData !== movementsList && graphData.length > 0) {
              const gBalances = [];
              let gBalance = stockQty;
              balanceMap = new Map();
              for (const movement of graphData) {
                gBalances.push(gBalance);
                balanceMap.set(balKey(movement), gBalance);
                if (movement.movement_type !== 'stock_count_report') {
                  gBalance = gBalance - (parseFloat(movement.total_quantity) || 0);
                }
              }
              setGraphMovements(graphData);
              setGraphBalances(gBalances);
              balanceMapRef.current = balanceMap;
              graphKeyRef.current = graphKey;
            } else {
              // Full-history fetch failed → fall back to the paginated chart data and
              // don't cache, so the next filter change re-attempts the full fetch.
              balanceMap = null;
              setGraphMovements([]);
              setGraphBalances([]);
              balanceMapRef.current = null;
              graphKeyRef.current = null;
            }
          } else {
            // ---- Only the type filter / page changed → reuse the cached ledger ----
            stockQty = currentStock != null ? currentStock : 0;
            balanceMap = balanceMapRef.current;
          }

          // Table running balances: look up each VISIBLE row's true balance from the full
          // ledger (correct under any type filter and across pages). Fall back to a
          // page-local walk only when the full ledger is unavailable (graph fetch failed).
          const balances = [];
          let walk = stockQty;
          for (const movement of movementsList) {
            const mapped = balanceMap ? balanceMap.get(balKey(movement)) : undefined;
            balances.push(mapped !== undefined ? mapped : walk);
            if (movement.movement_type !== 'stock_count_report') {
              walk = walk - (parseFloat(movement.total_quantity) || 0);
            }
          }
          if (seq !== loadSeqRef.current) return;
          setRunningBalances(balances);
        } catch (stockErr) {
          console.error('Failed to load current stock:', stockErr);
          // Non-critical error, continue without running balance
        }
      }
    } catch (err) {
      console.error('Failed to load movements:', err);
      // Only surface the banner if THIS is still the latest run — a stale run
      // that failed must not clobber a newer run's successfully-loaded data.
      if (seq === loadSeqRef.current) {
        setError('Failed to load inventory movements. Please try again.');
      }
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  };

  // The active trust point = the SPECIFIC count the stock baseline anchors on,
  // passed explicitly via from_count_date (e.g. from the Stock Breakdown dialog).
  // We only label that exact date — we never guess from the newest count, because
  // counts are not trusted and mislabelling an ordinary count as the trust point
  // would be misleading. No param → no label. (Vishal 2026-06-02)
  const activeTrustPointDate = useMemo(
    () => (fromCountDateParam ? String(fromCountDateParam).slice(0, 10) : null),
    [fromCountDateParam]
  );

  const handleSearch = () => {
    setPage(0);
    loadData();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleQuantityClick = async (movement) => {
    setSelectedMovement(movement);
    setDetailsError(null);
    setDetailsDialogOpen(true);
    setDetailsLoading(true);
    setOrderDetails([]);

    try {
      // Format date as YYYY-MM-DD
      const dateStr = typeof movement.movement_date === 'string'
        ? movement.movement_date.split('T')[0]
        : new Date(movement.movement_date).toISOString().split('T')[0];

      const params = {
        date: dateStr,
        sku: movement.sku,
        movement_type: movement.movement_type,
        warehouse_code: movement.warehouse_code,
        order_source: movement.order_source,
        limit: 500
      };

      const response = await getDailyMovementDetails(selectedBrand.id, params);
      setOrderDetails(response?.data?.details || []);
    } catch (err) {
      console.error('Failed to load order details:', err);
      setDetailsError(apiErrorMessage(err, 'Failed to load order details', { requestText: false }));
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedMovement(null);
    setOrderDetails([]);
    setDetailsError(null);
  };

  const handleDisposalEdit = async (detail) => {
    // Fetch the full disposal record to avoid overwriting fields with empty values
    try {
      const response = await getDisposal(detail.order_id);
      const record = response.data || response;
      setDisposalActionError(null); setDisposalEditDialog({
        open: true,
        id: detail.order_id,
        data: {
          disposal_type: record.disposal_type || detail.store_id || '',
          quantity: parseFloat(record.quantity) || parseFloat(detail.quantity) || '',
          unit_cost: record.unit_cost != null ? parseFloat(record.unit_cost) : '',
          batch_number: record.batch_number || '',
          reason: record.reason || '',
          notes: record.notes || ''
        }
      });
    } catch (err) {
      // Fallback to detail fields if fetch fails
      console.error('Failed to fetch disposal record, using mapped fields:', err);
      const custName = detail.customer_name || '';
      const colonIdx = custName.indexOf(': ');
      setDisposalActionError(null); setDisposalEditDialog({
        open: true,
        id: detail.order_id,
        data: {
          disposal_type: detail.store_id || '',
          quantity: parseFloat(detail.quantity) || '',
          unit_cost: '',
          batch_number: '',
          reason: colonIdx > -1 ? custName.substring(colonIdx + 2) : '',
          notes: ''
        }
      });
    }
  };

  const handleDisposalEditSave = async () => {
    try {
      setDisposalActionError(null);
      await updateDisposal(disposalEditDialog.id, disposalEditDialog.data);
      setDisposalActionSuccess('Disposal updated successfully');
      setDisposalEditDialog({ open: false, data: null, id: null });
      // Refresh the details popup
      if (selectedMovement) handleQuantityClick(selectedMovement);
      setTimeout(() => setDisposalActionSuccess(null), 3000);
    } catch (err) {
      setDisposalActionError(err.message || 'Failed to update disposal');
    }
  };

  const handleDisposalDelete = async () => {
    try {
      setDisposalActionError(null);
      await deleteDisposal(disposalDeleteDialog.detail.order_id);
      setDisposalActionSuccess('Disposal deleted. Stock restored.');
      setDisposalDeleteDialog({ open: false, detail: null });
      // Refresh the details popup
      if (selectedMovement) handleQuantityClick(selectedMovement);
      setTimeout(() => setDisposalActionSuccess(null), 3000);
    } catch (err) {
      setDisposalActionError(err.message || 'Failed to delete disposal');
    }
  };

  const getMovementIcon = (movementType) => {
    const config = MOVEMENT_TYPES[movementType];
    if (!config) return null;
    const Icon = config.icon;
    return <Icon fontSize="small" />;
  };

  const getMovementChip = (movementType) => {
    const config = MOVEMENT_TYPES[movementType];
    if (!config) return <Chip label={movementType} size="small" />;

    return (
      <Chip
        icon={getMovementIcon(movementType)}
        label={config.label}
        size="small"
        color={config.color}
      />
    );
  };

  const getQuantityDisplay = (quantity, movementType, clickable = false, onClick = null) => {
    const config = MOVEMENT_TYPES[movementType];
    const isPositive = config?.direction === 'in';
    const Icon = isPositive ? InIcon : OutIcon;
    const absQuantity = Math.abs(quantity);

    const content = (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Icon fontSize="small" color={isPositive ? 'success' : 'error'} />
        <Typography
          variant="body2"
          color={isPositive ? 'success.main' : 'error.main'}
          fontWeight="medium"
          sx={clickable ? {
            cursor: 'pointer',
            textDecoration: 'underline',
            '&:hover': { opacity: 0.8 }
          } : {}}
        >
          {isPositive ? '+' : '-'}{formatNumber(absQuantity)}
        </Typography>
      </Stack>
    );

    if (clickable && onClick) {
      return (
        <Tooltip title="Click to view order details">
          <Box onClick={onClick} sx={{ cursor: 'pointer' }}>
            {content}
          </Box>
        </Tooltip>
      );
    }

    return content;
  };

  const getSourceChip = (orderSource) => {
    if (!orderSource) {
      return <Typography variant="body2" color="text.secondary">N/A</Typography>;
    }

    const sourceLabel = orderSource === 'platform' ? 'Platform' :
                        orderSource === 'shipstation' ? 'ShipStation' :
                        orderSource;
    const chipColor = orderSource === 'platform' ? 'info' : 'default';

    return (
      <Chip
        label={sourceLabel}
        size="small"
        variant="outlined"
        color={chipColor}
      />
    );
  };

  const handleExportCSV = () => {
    if (movements.length === 0) {
      setError('No data to export');
      return;
    }

    // CSV headers
    const headers = ['Date', 'Movement Type', 'SKU', 'Warehouse', 'Total Quantity', 'Order Count', 'Source'];

    // CSV rows
    const rows = movements.map(m => [
      new Date(m.movement_date).toLocaleDateString(),
      MOVEMENT_TYPES[m.movement_type]?.label || m.movement_type,
      m.sku,
      m.warehouse_code,
      m.total_quantity,
      m.order_count,
      m.order_source || 'N/A'
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inventory_movements_daily_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && movements.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Brand Indicator */}
      {selectedBrand && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Viewing inventory movements for: <strong>{selectedBrand.name}</strong>
        </Alert>
      )}

      {!selectedBrand && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please select a brand to view inventory movements
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <InIcon color="success" />
                  <Typography variant="h6">{summary.inbound_count || 0}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Inbound Movements
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <OutIcon color="error" />
                  <Typography variant="h6">{summary.outbound_count || 0}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Outbound Movements
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ShipmentIcon color="primary" />
                  <Typography variant="h6">{summary.fulfillments_count || 0}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Order Fulfillments
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ReceiptIcon color="success" />
                  <Typography variant="h6">{summary.receipts_count || 0}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Goods Receipts
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search SKU"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Movement Type</InputLabel>
                <Select
                  value={movementTypeFilter}
                  onChange={(e) => setMovementTypeFilter(e.target.value)}
                  label="Movement Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {Object.entries(MOVEMENT_TYPES).map(([key, config]) => (
                    <MenuItem key={key} value={key}>
                      {config.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Warehouse</InputLabel>
                <Select
                  value={warehouseFilter}
                  onChange={(e) => setWarehouseFilter(e.target.value)}
                  label="Warehouse"
                >
                  <MenuItem value="all">All Warehouses</MenuItem>
                  {warehouses.map(wh => (
                    <MenuItem key={wh.code || wh.warehouse_code} value={wh.code || wh.warehouse_code}>
                      {wh.name || wh.warehouse_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="From Date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: '2000-01-01' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="To Date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: '2000-01-01' }}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <IconButton onClick={handleSearch} color="primary">
              <Tooltip title="Search">
                <SearchIcon />
              </Tooltip>
            </IconButton>
            <IconButton onClick={loadData} color="primary">
              <Tooltip title="Refresh">
                <RefreshIcon />
              </Tooltip>
            </IconButton>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={movements.length === 0}
            >
              Export CSV
            </Button>
            <Box sx={{ flex: 1 }} />
            {searchTerm && warehouseFilter !== 'all' && selectedBrand?.id && (
              <Tooltip title={`Open the Trust Point Wizard for ${searchTerm} @ ${warehouseFilter}`}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<TrustPointIcon />}
                  onClick={() => navigate(
                    `/inventory/trust-point-wizard?sku=${encodeURIComponent(searchTerm)}` +
                    `&warehouse=${encodeURIComponent(warehouseFilter)}` +
                    `&brand_id=${selectedBrand.id}` +
                    `&direct=1`
                  )}
                >
                  Allocate Trust Point
                </Button>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Stock Movements Chart - Only when SKU + warehouse selected */}
      {runningBalances.length > 0 && movements.length > 0 && (() => {
        // Use full unpaginated graph data when available (trust point mode),
        // otherwise fall back to paginated movements
        const chartMovements = graphMovements.length > 0 ? graphMovements : movements;
        const chartBalances = graphBalances.length > 0 ? graphBalances : runningBalances;

        // Create chart data - reverse for chronological order (oldest first)
        // First pass: create basic data with years
        const rawChartData = chartMovements.map((movement, idx) => {
          const dateObj = new Date(movement.movement_date);
          const isStockCount = movement.movement_type === 'stock_count_report';
          return {
            dateObj,
            fullDate: movement.movement_date,
            year: dateObj.getFullYear(),
            balance: isStockCount ? null : chartBalances[idx],
            calculatedBalance: chartBalances[idx],  // Always store for tooltip comparison
            reportedStock: isStockCount ? movement.total_quantity : null,
            quantity: movement.total_quantity,
            type: movement.movement_type,
            isStockCount,
            isAdjustment: ['adjustment', 'physical_count', 'adjustment_increase', 'adjustment_decrease', 'shiphero_adjustment'].includes(movement.movement_type)
          };
        }).reverse();

        // Per-row label: show year only on the first row of each calendar year, so the
        // axis isn't cluttered ("Sep 1 '24, Oct 5, Nov 12, Dec 1, Jan 1 '25, ..."). To
        // guarantee year-bearing labels actually render (Recharts auto-samples ticks
        // and used to silently drop them), we also pass an explicit `ticks` array to
        // XAxis below — built from year-boundaries + ~14 evenly-spaced samples.
        const chartData = rawChartData.map((item, idx, arr) => {
          const prev = idx > 0 ? arr[idx - 1] : null;
          const yearChanged = !prev || prev.dateObj.getFullYear() !== item.dateObj.getFullYear();
          return {
            ...item,
            date: yearChanged
              ? item.dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
              : item.dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          };
        });

        // Map fullDate (unique YYYY-MM-DD) → friendly label for X-axis ticks.
        // We key the X-axis on fullDate (not the friendly label) so ReferenceLine
        // can match a specific date unambiguously — labels like "Apr 21" can
        // repeat across years, fullDate cannot.
        const labelByFullDate = new Map();
        chartData.forEach(d => labelByFullDate.set(d.fullDate, d.date));

        // Explicit tick selection: union of (a) first row of each calendar year — the
        // year-bearing labels — and (b) ~14 evenly-spaced samples for density. Without
        // this, Recharts auto-samples and can drop the year-boundary ticks, leaving the
        // user with bare "Sep 1 / Oct 5 / Jan 12" labels and no idea what year they're in.
        const xAxisTicks = (() => {
          if (chartData.length === 0) return [];
          const required = new Set([chartData[0].fullDate, chartData[chartData.length - 1].fullDate]);
          let prevYear = null;
          chartData.forEach(d => {
            const y = d.dateObj.getFullYear();
            if (y !== prevYear) { required.add(d.fullDate); prevYear = y; }
          });
          const target = 14;
          const stride = Math.max(1, Math.floor(chartData.length / target));
          for (let i = 0; i < chartData.length; i += stride) required.add(chartData[i].fullDate);
          return chartData.filter(d => required.has(d.fullDate)).map(d => d.fullDate);
        })();

        // Find the exact fullDate in chart data that matches the trust point.
        // Used as ReferenceLine x — must equal an XAxis category value exactly.
        const tpFullDate = fromCountDateParam
          ? chartData.find(d => d.fullDate && String(d.fullDate).startsWith(fromCountDateParam))?.fullDate
          : null;

        return (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Stock Level History
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {searchTerm} @ {warehouseFilter} | Current Stock: {formatNumber(currentStock)}
                {fromCountDateParam && ` (from count ${new Date(fromCountDateParam).toLocaleDateString()})`}
                {movementTypeFilter !== 'all' && (
                  <Box component="span" sx={{ ml: 1, color: '#b45309', fontWeight: 600 }}>
                    · Highlighting {MOVEMENT_TYPES[movementTypeFilter]?.label || movementTypeFilter} (full history still shown)
                  </Box>
                )}
              </Typography>
              <Box sx={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <LineChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="fullDate"
                      tick={{ fontSize: 11 }}
                      tickMargin={10}
                      ticks={xAxisTicks}
                      interval={0}
                      tickFormatter={(value) => labelByFullDate.get(value) || value}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => formatNumber(value)}
                      domain={['auto', 'auto']}
                    />
                    <RechartsTooltip
                      formatter={(value, name, props) => {
                        if (value === null || value === undefined) return null;
                        if (name === 'reportedStock') {
                          const data = props?.payload;
                          const calc = data?.calculatedBalance;
                          if (calc !== null && calc !== undefined) {
                            const diff = Math.round(value) - Math.round(calc);
                            const diffStr = diff === 0 ? 'match' : `${diff > 0 ? '+' : ''}${formatNumber(diff)} diff`;
                            return [`${formatNumber(value)}  (Calculated: ${formatNumber(calc)}, ${diffStr})`, 'Reported Stock'];
                          }
                          return [formatNumber(value), 'Reported Stock'];
                        }
                        return [formatNumber(value), name === 'balance' ? 'Stock Level' : name];
                      }}
                      labelFormatter={(label, payload) => {
                        if (payload && payload.length > 0) {
                          const data = payload[0].payload;
                          const typeLabel = MOVEMENT_TYPES[data.type]?.label || data.type;
                          const fullDateLabel = data.dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                          if (data.isStockCount) {
                            return `${fullDateLabel} - ${typeLabel}: Reported ${formatNumber(data.quantity)}`;
                          }
                          return `${fullDateLabel} - ${typeLabel}: ${data.quantity >= 0 ? '+' : ''}${formatNumber(data.quantity)}`;
                        }
                        return label;
                      }}
                      contentStyle={{ fontSize: 12 }}
                    />
                    {tpFullDate && (
                      <ReferenceLine
                        x={tpFullDate}
                        stroke="#16a34a"
                        strokeDasharray="6 3"
                        strokeWidth={2}
                        label={{
                          value: `Trust Point · ${formatNumber(currentStock)} on ${new Date(fromCountDateParam).toLocaleDateString()}`,
                          position: 'insideTopRight',
                          fill: '#16a34a',
                          fontSize: 11,
                          fontWeight: 600
                        }}
                      />
                    )}
                    <Line
                      type="stepAfter"
                      dataKey="balance"
                      stroke="#1976d2"
                      strokeWidth={2}
                      connectNulls
                      isAnimationActive={false}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        if (payload.isStockCount || !cx || !cy) return null;
                        // When a movement-type filter is active, highlight matching points
                        // (gold ring + larger) and dim the rest — the line SHAPE never
                        // changes, only the emphasis. (2026-06-24)
                        const filterActive = movementTypeFilter !== 'all';
                        const isMatch = filterActive && payload.type === movementTypeFilter;
                        const dimmed = filterActive && !isMatch;
                        // Highlight adjustments with a different marker
                        if (payload.isAdjustment) {
                          return (
                            <svg key={`dot-${cx}-${cy}`} style={{ overflow: 'visible' }}>
                              {isMatch && <circle cx={cx} cy={cy} r={10} fill="none" stroke="#fbbf24" strokeWidth={3} />}
                              <circle cx={cx} cy={cy} r={isMatch ? 7 : 6} fill="#ff9800" stroke="#f57c00" strokeWidth={2} opacity={dimmed ? 0.2 : 1} />
                            </svg>
                          );
                        }
                        // Normal dots based on movement direction
                        const color = payload.quantity > 0 ? '#4caf50' : '#f44336';
                        return (
                          <svg key={`dot-${cx}-${cy}`} style={{ overflow: 'visible' }}>
                            {isMatch && <circle cx={cx} cy={cy} r={9} fill="none" stroke="#fbbf24" strokeWidth={3} />}
                            <circle cx={cx} cy={cy} r={isMatch ? 6 : 4} fill={color} opacity={dimmed ? 0.2 : 1} />
                          </svg>
                        );
                      }}
                      activeDot={{ r: 6, strokeWidth: 2 }}
                    />
                    {/* Stock count reported values — diamond markers at the reported quantity */}
                    <Line
                      type="monotone"
                      dataKey="reportedStock"
                      stroke="none"
                      strokeWidth={0}
                      connectNulls={false}
                      isAnimationActive={false}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        if (!payload.isStockCount || !cx || !cy) return null;
                        // Match the same highlight/dim treatment as the balance-line dots.
                        const filterActive = movementTypeFilter !== 'all';
                        const isMatch = filterActive && movementTypeFilter === 'stock_count_report';
                        const dimmed = filterActive && !isMatch;
                        const s = isMatch ? 11 : 8;
                        // Diamond shape for stock count reports
                        return (
                          <svg key={`count-${cx}-${cy}`} style={{ overflow: 'visible' }}>
                            {isMatch && (
                              <polygon
                                points={`${cx},${cy-s-4} ${cx+s+4},${cy} ${cx},${cy+s+4} ${cx-s-4},${cy}`}
                                fill="none"
                                stroke="#fbbf24"
                                strokeWidth={3}
                              />
                            )}
                            <polygon
                              points={`${cx},${cy-s} ${cx+s},${cy} ${cx},${cy+s} ${cx-s},${cy}`}
                              fill="#9c27b0"
                              stroke="#7b1fa2"
                              strokeWidth={2}
                              opacity={dimmed ? 0.2 : 1}
                            />
                          </svg>
                        );
                      }}
                      activeDot={{ r: 8, strokeWidth: 2, fill: '#9c27b0', stroke: '#7b1fa2' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, mt: 1, justifyContent: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50' }} />
                  <Typography variant="caption">Stock In</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f44336' }} />
                  <Typography variant="caption">Stock Out</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800', border: '2px solid #f57c00' }} />
                  <Typography variant="caption">Adjustment/Count</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: '#9c27b0', border: '2px solid #7b1fa2', transform: 'rotate(45deg)' }} />
                  <Typography variant="caption">Reported Stock</Typography>
                </Box>
                {tpFullDate && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 16, height: 0, borderTop: '2px dashed #16a34a' }} />
                    <Typography variant="caption">Trust Point</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        );
      })()}

      {/* Movements Table - Daily Aggregated */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Inventory Movements Audit Trail (Daily Summary)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click on the quantity to view individual order details
            {currentStock !== null && (
              <> | <strong>Current Stock: {formatNumber(currentStock)}</strong> (running balance shown)</>
            )}
            {currentStock === null && searchTerm && warehouseFilter === 'all' && (
              <> | <em>Select a warehouse to see running balance</em></>
            )}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Movement Type</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Warehouse</TableCell>
                  <TableCell align="right">Total Quantity</TableCell>
                  {runningBalances.length > 0 && (
                    <TableCell align="right">
                      <Tooltip title="Stock balance after this movement (requires SKU + Warehouse filter)">
                        <span>Running Balance</span>
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell align="right">Orders</TableCell>
                  <TableCell>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={runningBalances.length > 0 ? 9 : 8} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        No inventory movements found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  movements.map((movement, idx) => {
                    const isTrustPoint = movement.movement_type === 'stock_count_report'
                      && activeTrustPointDate
                      && String(movement.movement_date).slice(0, 10) === activeTrustPointDate;
                    return (
                    <TableRow key={idx} hover selected={isTrustPoint}>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(movement.movement_date).toLocaleDateString()}
                        </Typography>
                        {isTrustPoint && (
                          <Chip
                            label="Trust point"
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.62rem', mt: 0.25 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {getMovementChip(movement.movement_type)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {movement.sku}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={movement.product_name || ''}>
                          {movement.product_name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getWarehouseName(movement.warehouse_code)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {movement.movement_type === 'stock_count_report' ? (
                          <Typography variant="body2" fontWeight="medium" color="#9c27b0">
                            Reported: {formatNumber(movement.total_quantity)}
                          </Typography>
                        ) : (
                          getQuantityDisplay(
                            movement.total_quantity,
                            movement.movement_type,
                            true,
                            () => handleQuantityClick(movement)
                          )
                        )}
                      </TableCell>
                      {runningBalances.length > 0 && (
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            color={movement.movement_type === 'stock_count_report'
                              ? (Math.abs(runningBalances[idx] - movement.total_quantity) > 0.5 ? 'warning.main' : 'text.primary')
                              : (runningBalances[idx] < 0 ? 'error.main' : 'text.primary')}
                          >
                            {formatNumber(runningBalances[idx])}
                            {movement.movement_type === 'stock_count_report' && Math.abs(runningBalances[idx] - movement.total_quantity) > 0.5 && (
                              <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.5 }}>
                                ({runningBalances[idx] - movement.total_quantity > 0 ? '+' : ''}{formatNumber(runningBalances[idx] - movement.total_quantity)})
                              </Typography>
                            )}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Chip
                          label={movement.order_count}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getSourceChip(movement.order_source)}
                          {(movement.movement_type === 'transfer_out' || movement.movement_type === 'transfer_in') && (
                            <Tooltip title="View Transfer">
                              <IconButton
                                size="small"
                                onClick={() => navigate(movement.transfer_id && movement.transfer_id !== '0'
                                  ? `/inventory/transfers/${movement.transfer_id}`
                                  : '/inventory/transfers')}
                                sx={{ ml: 0.5 }}
                              >
                                <OpenInNewIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[50, 100, 250, 500]}
          />
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedMovement?.movement_type === 'disposal' ? 'Disposal Details' : 'Order Details'} - {selectedMovement && new Date(selectedMovement.movement_date).toLocaleDateString()}
            </Typography>
            <IconButton onClick={handleCloseDetails} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
          {selectedMovement && (
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Chip label={selectedMovement.sku} size="small" />
              {getMovementChip(selectedMovement.movement_type)}
              <Typography variant="body2" color="text.secondary">
                Warehouse: {selectedMovement.warehouse_code || 'N/A'}
              </Typography>
            </Stack>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detailsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : detailsError ? (
            <Alert severity="error">{detailsError}</Alert>
          ) : orderDetails.length === 0 ? (
            <Typography color="text.secondary" align="center" py={4}>
              No order details available
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {selectedMovement?.movement_type === 'disposal' ? 'Disposal ID' : 'Order ID'}
                    </TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell>
                      {selectedMovement?.movement_type === 'transfer_out' || selectedMovement?.movement_type === 'transfer_in'
                        ? 'Transfer Route'
                        : selectedMovement?.movement_type === 'disposal'
                          ? 'Disposal Type'
                          : 'Store'}
                    </TableCell>
                    {selectedMovement?.movement_type === 'disposal' && (
                      <TableCell align="center">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderDetails.map((detail, idx) => {
                    // Parse all_products if it's a string
                    let products = [];
                    try {
                      if (detail.all_products) {
                        products = typeof detail.all_products === 'string'
                          ? JSON.parse(detail.all_products)
                          : detail.all_products;
                      }
                    } catch (e) {
                      products = [];
                    }

                    // Build tooltip content — different for disposals vs orders
                    const isDisposal = detail.order_source === 'disposal';
                    const tooltipContent = isDisposal ? (
                      <Box sx={{ p: 1, maxWidth: 350 }}>
                        {detail.customer_name && detail.customer_name.trim() && (
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            {detail.customer_name.trim()}
                          </Typography>
                        )}
                        {detail.customer_email && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            Approved by: {detail.customer_email}
                          </Typography>
                        )}
                        {detail.all_products && detail.all_products.trim() && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            {detail.all_products}
                          </Typography>
                        )}
                        {!detail.customer_name?.trim() && !detail.customer_email && !detail.all_products?.trim() && (
                          <Typography variant="caption" color="text.secondary">
                            No additional details
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ p: 1, maxWidth: 350 }}>
                        {detail.customer_name && detail.customer_name.trim() && (
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            {detail.customer_name.trim()}
                          </Typography>
                        )}
                        {detail.customer_email && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            {detail.customer_email}
                          </Typography>
                        )}
                        {products.length > 0 && (
                          <>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                              Products ({products.length}):
                            </Typography>
                            {products.slice(0, 5).map((p, pIdx) => (
                              <Typography key={pIdx} variant="caption" display="block" sx={{ pl: 1 }}>
                                • {p.product_name || p.name || p.sku || 'Unknown'} x {p.product_name_quantity_sold || p.quantity || 1}
                              </Typography>
                            ))}
                            {products.length > 5 && (
                              <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                                ... and {products.length - 5} more
                              </Typography>
                            )}
                          </>
                        )}
                        {!detail.customer_name?.trim() && !detail.customer_email && products.length === 0 && (
                          <Typography variant="caption" color="text.secondary">
                            No additional details available
                          </Typography>
                        )}
                      </Box>
                    );

                    return (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Tooltip
                            title={tooltipContent}
                            arrow
                            placement="right"
                            enterDelay={300}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: 'background.paper',
                                  color: 'text.primary',
                                  boxShadow: 3,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  '& .MuiTooltip-arrow': {
                                    color: 'background.paper',
                                    '&::before': {
                                      border: '1px solid',
                                      borderColor: 'divider',
                                    },
                                  },
                                },
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color="primary"
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                              onClick={
                                (selectedMovement?.movement_type === 'transfer_out' || selectedMovement?.movement_type === 'transfer_in') && detail.reference_id
                                  ? (e) => { e.stopPropagation(); navigate(`/inventory/transfers/${detail.reference_id}`); }
                                  : undefined
                              }
                            >
                              {detail.order_name || detail.reference_number || 'N/A'}
                              {(selectedMovement?.movement_type === 'transfer_out' || selectedMovement?.movement_type === 'transfer_in') && detail.reference_id && (
                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                              )}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {detail.sku}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatNumber(detail.quantity)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {detail.from_warehouse && detail.to_warehouse ? (
                            <Typography variant="caption" color="text.secondary">
                              {detail.from_warehouse} → {detail.to_warehouse}
                              {detail.transfer_status && detail.transfer_status !== 'completed' && (
                                <Chip
                                  label={detail.transfer_status}
                                  size="small"
                                  sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                                  color={detail.transfer_status === 'in_transit' ? 'warning' : 'default'}
                                />
                              )}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {detail.store_id || detail.warehouse_code || 'N/A'}
                            </Typography>
                          )}
                        </TableCell>
                        {selectedMovement?.movement_type === 'disposal' && (
                          <TableCell align="center">
                            <Tooltip title="Edit disposal">
                              <IconButton size="small" onClick={() => handleDisposalEdit(detail)}>
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete disposal">
                              <IconButton size="small" color="error" onClick={() => { setDisposalActionError(null); setDisposalDeleteDialog({ open: true, detail }); }}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        {disposalActionError && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setDisposalActionError(null)}>{disposalActionError}</Alert>
        )}
        {disposalActionSuccess && (
          <Alert severity="success" sx={{ mt: 1 }} onClose={() => setDisposalActionSuccess(null)}>{disposalActionSuccess}</Alert>
        )}
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Disposal Edit Dialog */}
      <Dialog open={disposalEditDialog.open} onClose={() => setDisposalEditDialog({ open: false, data: null, id: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Disposal #{disposalEditDialog.id}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {disposalActionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDisposalActionError(null)}>{disposalActionError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Disposal Type</InputLabel>
              <Select
                value={disposalEditDialog.data?.disposal_type || ''}
                label="Disposal Type"
                onChange={(e) => setDisposalEditDialog(prev => ({ ...prev, data: { ...prev.data, disposal_type: e.target.value } }))}
              >
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="damaged">Damaged</MenuItem>
                <MenuItem value="write_off">Write Off</MenuItem>
                <MenuItem value="sample">Sample/Gift</MenuItem>
                <MenuItem value="theft">Theft/Loss</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Quantity"
              type="number"
              size="small"
              value={disposalEditDialog.data?.quantity || ''}
              onChange={(e) => setDisposalEditDialog(prev => ({ ...prev, data: { ...prev.data, quantity: parseInt(e.target.value) || '' } }))}
              fullWidth
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Reason"
              size="small"
              value={disposalEditDialog.data?.reason || ''}
              onChange={(e) => setDisposalEditDialog(prev => ({ ...prev, data: { ...prev.data, reason: e.target.value } }))}
              fullWidth
            />
            <TextField
              label="Notes"
              size="small"
              value={disposalEditDialog.data?.notes || ''}
              onChange={(e) => setDisposalEditDialog(prev => ({ ...prev, data: { ...prev.data, notes: e.target.value } }))}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisposalEditDialog({ open: false, data: null, id: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleDisposalEditSave} disabled={!disposalEditDialog.data?.quantity}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Disposal Delete Confirmation */}
      <Dialog open={disposalDeleteDialog.open} onClose={() => setDisposalDeleteDialog({ open: false, detail: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Disposal</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {disposalActionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDisposalActionError(null)}>{disposalActionError}</Alert>}
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will permanently delete this disposal and restore {disposalDeleteDialog.detail?.quantity || 0} units back to stock.
          </Alert>
          {disposalDeleteDialog.detail && (
            <Box>
              <Typography variant="body2"><strong>Disposal ID:</strong> {disposalDeleteDialog.detail.order_id}</Typography>
              <Typography variant="body2"><strong>SKU:</strong> {disposalDeleteDialog.detail.sku}</Typography>
              <Typography variant="body2"><strong>Quantity:</strong> {disposalDeleteDialog.detail.quantity}</Typography>
              <Typography variant="body2"><strong>Type:</strong> {disposalDeleteDialog.detail.store_id}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisposalDeleteDialog({ open: false, detail: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDisposalDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InventoryMovements;
